import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Switch,
  Typography,
  Divider,
  FormControlLabel,
} from '@mui/material';
import { StaffProfile, User } from '../../services/staffService';

export type StaffFormState = Partial<StaffProfile>;

interface StaffFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  value: StaffFormState;
  onChange: (next: StaffFormState) => void;
  onSave: () => void | Promise<void>;
  onClose: () => void;
  users?: User[];
  errors?: Record<string, string>;
  saveLabel?: string;
}

/**
 * Single source of truth for the Create/Edit Staff dialog.
 * Used by both StaffPage and StaffDetailPage so payroll, address, and
 * emergency-contact fields stay in sync across entry points.
 */
const StaffFormDialog: React.FC<StaffFormDialogProps> = ({
  open,
  mode,
  value,
  onChange,
  onSave,
  onClose,
  users = [],
  errors = {},
  saveLabel,
}) => {
  const set = (patch: StaffFormState) => onChange({ ...value, ...patch });
  const isCreate = mode === 'create';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isCreate ? 'Create Staff Profile' : 'Edit Staff Profile'}
        {isCreate && (
          <Typography variant="caption" display="block" color="text.secondary">
            Employee ID will be auto-generated (e.g., EMP20250001)
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* ── Identity (only shown when creating — links to a user) ── */}
          {isCreate && (
            <FormControl fullWidth error={!!errors.userId} required>
              <InputLabel>User</InputLabel>
              <Select
                label="User"
                value={value.userId ?? ''}
                onChange={(e) => {
                  const userId = Number(e.target.value);
                  const u = users.find((x) => x.id === userId);
                  set({
                    userId,
                    firstName: u?.firstName || value.firstName || '',
                    lastName: u?.lastName || value.lastName || '',
                    email: u?.email || value.email || '',
                  });
                }}
              >
                <MenuItem value=""><em>Select a user (required)</em></MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.firstName} {u.lastName} ({u.email})
                  </MenuItem>
                ))}
              </Select>
              {errors.userId && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.userId}
                </Typography>
              )}
            </FormControl>
          )}

          <Stack direction="row" spacing={2}>
            <TextField
              label="First Name"
              value={value.firstName || ''}
              onChange={(e) => set({ firstName: e.target.value })}
              fullWidth required
              disabled={isCreate && !!value.userId}
              error={!!errors.firstName}
              helperText={errors.firstName || (isCreate && value.userId ? 'Auto-filled from selected user' : '')}
            />
            <TextField
              label="Last Name"
              value={value.lastName || ''}
              onChange={(e) => set({ lastName: e.target.value })}
              fullWidth required
              disabled={isCreate && !!value.userId}
              error={!!errors.lastName}
              helperText={errors.lastName || (isCreate && value.userId ? 'Auto-filled from selected user' : '')}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Email"
              type="email"
              value={value.email || ''}
              onChange={(e) => set({ email: e.target.value })}
              fullWidth required
              disabled={isCreate && !!value.userId}
              error={!!errors.email}
              helperText={errors.email}
            />
            <TextField
              label="Phone Number"
              value={value.phoneNumber || ''}
              onChange={(e) => set({ phoneNumber: e.target.value })}
              fullWidth required
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber}
            />
          </Stack>

          {/* ── Role ── */}
          <Divider><Typography variant="caption" color="text.secondary">Role & Compensation</Typography></Divider>
          <Stack direction="row" spacing={2}>
            <FormControl fullWidth error={!!errors.department}>
              <InputLabel>Department</InputLabel>
              <Select
                label="Department"
                value={value.department || 'care'}
                onChange={(e) => set({ department: e.target.value as string })}
              >
                <MenuItem value="nursing">Nursing</MenuItem>
                <MenuItem value="care">Care Services</MenuItem>
                <MenuItem value="admin">Administration</MenuItem>
                <MenuItem value="management">Management</MenuItem>
                <MenuItem value="support">Support Services</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth error={!!errors.position}>
              <InputLabel>Position</InputLabel>
              <Select
                label="Position"
                value={value.position || 'care_worker'}
                onChange={(e) => set({ position: e.target.value as string })}
              >
                <MenuItem value="care_worker">Care Worker</MenuItem>
                <MenuItem value="registered_nurse">Registered Nurse</MenuItem>
                <MenuItem value="social_worker">Social Worker</MenuItem>
                <MenuItem value="support_worker">Support Worker</MenuItem>
                <MenuItem value="team_leader">Team Leader</MenuItem>
                <MenuItem value="supervisor">Supervisor</MenuItem>
                <MenuItem value="coordinator">Coordinator</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Employment Type</InputLabel>
              <Select
                label="Employment Type"
                value={value.employmentType || 'full_time'}
                onChange={(e) => set({ employmentType: e.target.value as string })}
              >
                <MenuItem value="full_time">Full Time</MenuItem>
                <MenuItem value="part_time">Part Time</MenuItem>
                <MenuItem value="casual">Casual</MenuItem>
                <MenuItem value="contractor">Contractor</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Hourly Rate ($)"
              type="number"
              value={value.hourlyRate ?? ''}
              onChange={(e) => set({ hourlyRate: parseFloat(e.target.value) || 0 })}
              fullWidth required
              inputProps={{ min: 0.01, max: 999.99, step: 0.01 }}
              error={!!errors.hourlyRate}
              helperText={errors.hourlyRate}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Start Date"
              type="date"
              value={value.startDate ? value.startDate.substring(0, 10) : ''}
              onChange={(e) => set({ startDate: e.target.value })}
              fullWidth required
              error={!!errors.startDate}
              helperText={errors.startDate}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Date of Birth"
              type="date"
              value={value.dateOfBirth ? value.dateOfBirth.substring(0, 10) : ''}
              onChange={(e) => set({ dateOfBirth: e.target.value })}
              fullWidth required
              error={!!errors.dateOfBirth}
              helperText={errors.dateOfBirth}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>

          {/* ── Address ── */}
          <Divider><Typography variant="caption" color="text.secondary">Address</Typography></Divider>
          <TextField
            label="Address Line 1"
            value={value.addressLine1 || ''}
            onChange={(e) => set({ addressLine1: e.target.value })}
            fullWidth required
            error={!!errors.addressLine1}
            helperText={errors.addressLine1}
          />
          <TextField
            label="Address Line 2 (Optional)"
            value={value.addressLine2 || ''}
            onChange={(e) => set({ addressLine2: e.target.value })}
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="City"
              value={value.city || ''}
              onChange={(e) => set({ city: e.target.value })}
              fullWidth required
              error={!!errors.city}
              helperText={errors.city}
            />
            <TextField
              label="State"
              value={value.state || ''}
              onChange={(e) => set({ state: e.target.value })}
              fullWidth required
              error={!!errors.state}
              helperText={errors.state}
            />
            <TextField
              label="Postal Code"
              value={value.postalCode || ''}
              onChange={(e) => set({ postalCode: e.target.value })}
              fullWidth required
              error={!!errors.postalCode}
              helperText={errors.postalCode}
            />
          </Stack>

          {/* ── Emergency Contact ── */}
          <Divider><Typography variant="caption" color="text.secondary">Emergency Contact</Typography></Divider>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Name"
              value={value.emergencyContactName || ''}
              onChange={(e) => set({ emergencyContactName: e.target.value })}
              fullWidth required
              error={!!errors.emergencyContactName}
              helperText={errors.emergencyContactName}
            />
            <TextField
              label="Phone"
              value={value.emergencyContactPhone || ''}
              onChange={(e) => set({ emergencyContactPhone: e.target.value })}
              fullWidth required
              error={!!errors.emergencyContactPhone}
              helperText={errors.emergencyContactPhone}
            />
          </Stack>
          <TextField
            label="Relationship"
            value={value.emergencyContactRelationship || ''}
            onChange={(e) => set({ emergencyContactRelationship: e.target.value })}
            fullWidth
          />

          {/* ── Qualifications ── */}
          <Divider><Typography variant="caption" color="text.secondary">Qualifications</Typography></Divider>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Qualifications"
              value={value.qualifications || ''}
              onChange={(e) => set({ qualifications: e.target.value })}
              fullWidth multiline rows={2}
              helperText="Education, degrees, etc."
            />
            <TextField
              label="Certifications"
              value={value.certifications || ''}
              onChange={(e) => set({ certifications: e.target.value })}
              fullWidth multiline rows={2}
              helperText="Professional certifications"
            />
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="Preferred Hours Per Week"
              type="number"
              value={value.preferredHoursPerWeek ?? ''}
              onChange={(e) => set({ preferredHoursPerWeek: e.target.value ? parseInt(e.target.value, 10) : undefined })}
              inputProps={{ min: 0, max: 80 }}
              sx={{ width: 240 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!value.availableWeekdays}
                  onChange={(e) => set({ availableWeekdays: e.target.checked })}
                />
              }
              label="Weekdays"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!value.availableWeekends}
                  onChange={(e) => set({ availableWeekends: e.target.checked })}
                />
              }
              label="Weekends"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!value.availableNights}
                  onChange={(e) => set({ availableNights: e.target.checked })}
                />
              }
              label="Nights"
            />
          </Stack>

          {/* ── Tax Declaration (ATO PAYG) ── */}
          <Divider>
            <Typography variant="caption" color="text.secondary">Tax Declaration (ATO PAYG)</Typography>
          </Divider>
          <Typography variant="caption" color="text.secondary">
            Drives PAYG withholding (NAT 1004 / NAT 3539, FY 2025-26) and STP Phase 2 reporting.
          </Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Tax File Number (TFN)"
              value={value.taxFileNumber || ''}
              onChange={(e) => set({ taxFileNumber: e.target.value })}
              fullWidth
              helperText="9 digits — leave blank if no TFN provided (47% withholding applies)"
            />
            <TextField
              label="ABN (contractor / WHM)"
              value={value.abn || ''}
              onChange={(e) => set({ abn: e.target.value })}
              fullWidth
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Residency Status</InputLabel>
              <Select
                label="Residency Status"
                value={value.residencyStatus || 'Resident'}
                onChange={(e) => set({ residencyStatus: e.target.value as string })}
              >
                <MenuItem value="Resident">Australian Resident</MenuItem>
                <MenuItem value="ForeignResident">Foreign Resident</MenuItem>
                <MenuItem value="WorkingHoliday">Working Holiday Maker (Subclass 417/462)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Visa Subclass"
              value={value.visaSubclass || ''}
              onChange={(e) => set({ visaSubclass: e.target.value })}
              fullWidth
              placeholder="417 or 462"
            />
          </Stack>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <FormControlLabel
              control={
                <Switch
                  checked={value.hasTfn ?? true}
                  onChange={(e) => set({ hasTfn: e.target.checked })}
                />
              }
              label="TFN provided"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={value.claimsTaxFreeThreshold ?? true}
                  onChange={(e) => set({ claimsTaxFreeThreshold: e.target.checked })}
                />
              }
              label="Claims Tax-Free Threshold"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={value.isWorkingHoliday ?? false}
                  onChange={(e) => set({ isWorkingHoliday: e.target.checked })}
                />
              }
              label="Working Holiday Maker"
            />
          </Stack>

          {/* ── Bank Disbursement ── */}
          <Divider>
            <Typography variant="caption" color="text.secondary">Bank Account (Net-Pay Disbursement)</Typography>
          </Divider>
          <TextField
            label="Account Name"
            value={value.bankAccountName || ''}
            onChange={(e) => set({ bankAccountName: e.target.value })}
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="BSB"
              value={value.bankBsb || ''}
              onChange={(e) => set({ bankBsb: e.target.value })}
              fullWidth
              placeholder="e.g. 062-001"
            />
            <TextField
              label="Account Number"
              value={value.bankAccountNumber || ''}
              onChange={(e) => set({ bankAccountNumber: e.target.value })}
              fullWidth
            />
          </Stack>

          {/* ── Superannuation ── */}
          <Divider>
            <Typography variant="caption" color="text.secondary">Superannuation Fund (12% SG)</Typography>
          </Divider>
          <Typography variant="caption" color="text.secondary">
            Employer pays 12% of OTE directly to the fund — not deducted from gross.
          </Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Fund Name"
              value={value.superFundName || ''}
              onChange={(e) => set({ superFundName: e.target.value })}
              fullWidth
            />
            <TextField
              label="Fund USI"
              value={value.superFundUsi || ''}
              onChange={(e) => set({ superFundUsi: e.target.value })}
              fullWidth
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Fund ABN"
              value={value.superFundAbn || ''}
              onChange={(e) => set({ superFundAbn: e.target.value })}
              fullWidth
            />
            <TextField
              label="Member Number"
              value={value.superMemberNumber || ''}
              onChange={(e) => set({ superMemberNumber: e.target.value })}
              fullWidth
            />
          </Stack>

          {/* ── Active toggle (edit only) ── */}
          {!isCreate && (
            <FormControlLabel
              control={
                <Switch
                  checked={value.isActive ?? true}
                  onChange={(e) => set({ isActive: e.target.checked })}
                />
              }
              label="Active Staff Member"
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onSave} variant="contained">
          {saveLabel ?? (isCreate ? 'Create' : 'Save Changes')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StaffFormDialog;
